import FieldsRepeaterItem from '@components/FieldsRepeater/FieldsRepeaterItem'
import { Button } from 'primereact/button'
import { useEffect, useState } from 'react'

/**
 * Fields repeater component.
 *
 * @component
 * @param {Object} props
 * @param {string} props.title
 * @param {string} [props.buttonLabel="Add"]
 * @param {number} [props.max]
 * @param {Object} props.model
 * @param {Array} [props.data=[]]
 * @param {Function} props.setData
 * @param {boolean} [props.processing=false]
 * @param {boolean} [props.loading=false]
 * @param {Object} [props.errors={}]
 * @param {string} [props.fieldKey] - Field name, used to build the container's CSS class.
 * @returns {Element}
 */
const FieldsRepeater = ({
  title,
  buttonLabel = 'Add',
  max,
  model,
  data = [],
  setData,
  processing = false,
  loading = false,
  errors = {},
  fieldKey
}) => {
  /**
   * We need to manage local errors state to be able to remove item errors
   * when an item is removed, and to shift errors for subsequent items
   */
  const [fieldsErrors, setFieldsErrors] = useState(errors)

  useEffect(() => {
    setFieldsErrors(errors)
  }, [errors])
  /**
   * Add item.
   *
   * @param {Event} e
   * @returns {void}
   */
  const add = e => {
    e && e.preventDefault()
    setData([...data, {}])
  }
  /**
   * Update item data.
   *
   * @param {any} itemData
   * @param {number} itemDataIndex
   * @returns {void}
   */
  const updateItemData = (itemData, itemDataIndex) => {
    const updatedData = data.map((item, index) => (itemDataIndex === index ? itemData : item))
    setData(updatedData)
  }

  /**
   * Remove item data.
   *
   * @param {number} itemDataIndex
   * @returns {void}
   */
  const removeItemData = itemDataIndex => {
    const updatedData = data.filter((item, index) => index !== itemDataIndex)
    setData(updatedData)
    // Remove item errors
    setFieldsErrors(prevErrors => {
      const updatedErrors = { ...prevErrors }
      delete updatedErrors[itemDataIndex]
      // Shift errors for subsequent items
      for (let i = itemDataIndex + 1; i <= Object.keys(prevErrors).length; i++) {
        if (prevErrors[i]) {
          updatedErrors[i - 1] = prevErrors[i]
          delete updatedErrors[i]
        }
      }
      return updatedErrors
    })
  }

  return (
    <div className={`fields-repeater field-${fieldKey}`}>
      <div className="fields-repeater--title">{title}</div>
      {!!data.length && (
        <div className="fields-repeater--items">
          {data.map((entry, index) => (
            <FieldsRepeaterItem
              key={index}
              index={index}
              model={model}
              data={entry}
              errors={fieldsErrors[index] ?? {}}
              setData={updateItemData}
              removeAction={removeItemData}
            />
          ))}
        </div>
      )}
      {(data.length < max || !max) && (
        <div className="fields-repeater--actions">
          <Button
            rounded
            severity="primary"
            loading={loading}
            disabled={processing}
            label={buttonLabel}
            icon="fa-regular fa-plus"
            onClick={add}
          />
        </div>
      )}
    </div>
  )
}

FieldsRepeater.displayName = 'FieldsRepeater'

export default FieldsRepeater
